import { ApiError } from '../live/api.ts';

/**
 * The Start gate's refusal, as it arrives over HTTP.
 *
 * Mirrors `DependencyBlock` in core. Declared here rather than imported because the dashboard
 * only maps `@nightshift/ui-types` from core, and the lifecycle types are server-side; the
 * shape is small and the decoder below refuses anything that does not match it.
 */
export interface DependencyBlockPayload {
  unmet: BlockedDependency[];
  /** Earlier milestones, by the milestone order, that still hold unfinished tickets. */
  milestones: BlockedMilestone[];
  proposals: BlockedProposal[];
  /**
   * Overlaps with a ticket somebody is working on right now.
   *
   * A third question, not a third kind of dependency: nothing here says this ticket cannot be
   * done, only that doing it beside the other one costs a rebase. The row's action is an
   * order, not a verdict.
   */
  sequencing: BlockedSequencing[];
  /**
   * How much comparison the ticket has had.
   *
   * Carried by a refusal and by a start that was allowed without one, because an empty
   * proposal list means "nothing found" and "nobody looked" alike, and the two lead a person
   * to do different things.
   */
  scanStatus?: BlockedScanStatus;
}

interface BlockedScanStatus {
  kind: string;
  detail: string;
  coverage?: 'complete' | 'partial' | 'unknown';
  notExamined?: number;
}

interface BlockedDependency {
  ticketId: number;
  ticketKey: string;
  state: string;
  unavailable: boolean;
  since: number;
}

interface BlockedMilestone {
  milestoneId: number;
  name: string;
  open: Array<{ ticketId: number; ticketKey: string; state: string }>;
}

interface BlockedProposal {
  id: number;
  inputRevision: number;
  ticketKey: string;
  band: string;
  reason: string;
}

interface BlockedSequencing {
  id: number;
  inputRevision: number;
  ticketId: number;
  ticketKey: string;
  /** Where the other ticket is, which is what "lands" means for this row. */
  state: string;
  band: string;
  reason: string;
  /** The files both tickets rewrite. */
  evidence: string[];
}

/**
 * Decode a 409 from Start into the gate's payload, or undefined for any other failure.
 *
 * Every field is checked rather than cast: this is the one place a server response drives a
 * dialog offering to override a safety check, and a half-decoded payload there would render
 * an override button next to a blank list.
 */
export function dependencyBlockOf(error: unknown): DependencyBlockPayload | undefined {
  if (!(error instanceof ApiError) || error.body === null) return undefined;
  const raw = error.body.dependencyBlocked;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  const unmet = Array.isArray(record.unmet) ? record.unmet.flatMap(toDependency) : [];
  const proposals = Array.isArray(record.proposals) ? record.proposals.flatMap(toProposal) : [];
  const sequencing = Array.isArray(record.sequencing) ? record.sequencing.flatMap(toSequencing) : [];
  const milestones = Array.isArray(record.milestones) ? record.milestones.flatMap(toMilestone) : [];
  if (unmet.length === 0 && milestones.length === 0 && proposals.length === 0 && sequencing.length === 0) {
    return undefined;
  }
  const scanStatus = toScanStatus(record.scanStatus);
  return { unmet, milestones, proposals, sequencing, ...(scanStatus ? { scanStatus } : {}) };
}

function toScanStatus(value: unknown): BlockedScanStatus | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const row = value as Record<string, unknown>;
  if (typeof row.kind !== 'string') return undefined;
  return {
    kind: row.kind,
    detail: typeof row.detail === 'string' ? row.detail : '',
    ...(row.coverage === 'complete' || row.coverage === 'partial' || row.coverage === 'unknown'
      ? { coverage: row.coverage }
      : {}),
    ...(typeof row.notExamined === 'number' ? { notExamined: row.notExamined } : {}),
  };
}

function toDependency(value: unknown): BlockedDependency[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return [];
  const row = value as Record<string, unknown>;
  if (typeof row.ticketKey !== 'string' || typeof row.state !== 'string') return [];
  return [
    {
      ticketId: typeof row.ticketId === 'number' ? row.ticketId : 0,
      ticketKey: row.ticketKey,
      state: row.state,
      unavailable: row.unavailable === true,
      since: typeof row.since === 'number' ? row.since : 0,
    },
  ];
}

function toMilestone(value: unknown): BlockedMilestone[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return [];
  const row = value as Record<string, unknown>;
  if (typeof row.milestoneId !== 'number' || typeof row.name !== 'string' || !Array.isArray(row.open)) return [];
  const open = row.open.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const ticket = entry as Record<string, unknown>;
    if (typeof ticket.ticketKey !== 'string') return [];
    return [{
      ticketId: typeof ticket.ticketId === 'number' ? ticket.ticketId : 0,
      ticketKey: ticket.ticketKey,
      state: typeof ticket.state === 'string' ? ticket.state : '',
    }];
  });
  return [{ milestoneId: row.milestoneId, name: row.name, open }];
}

function toProposal(value: unknown): BlockedProposal[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return [];
  const row = value as Record<string, unknown>;
  if (typeof row.id !== 'number' || typeof row.ticketKey !== 'string') return [];
  return [
    {
      id: row.id,
      inputRevision: typeof row.inputRevision === 'number' ? row.inputRevision : 1,
      ticketKey: row.ticketKey,
      band: typeof row.band === 'string' ? row.band : 'high',
      reason: typeof row.reason === 'string' ? row.reason : '',
    },
  ];
}

function toSequencing(value: unknown): BlockedSequencing[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return [];
  const row = value as Record<string, unknown>;
  if (typeof row.id !== 'number' || typeof row.ticketKey !== 'string') return [];
  // The id is what the accept posts back, so a row without one cannot offer the action that
  // clears it. Dropping it is better than rendering a button that would 400.
  if (typeof row.ticketId !== 'number' || row.ticketId < 1) return [];
  return [
    {
      id: row.id,
      inputRevision: typeof row.inputRevision === 'number' ? row.inputRevision : 1,
      ticketId: row.ticketId,
      ticketKey: row.ticketKey,
      state: typeof row.state === 'string' ? row.state : '',
      band: typeof row.band === 'string' ? row.band : 'medium',
      reason: typeof row.reason === 'string' ? row.reason : '',
      evidence: Array.isArray(row.evidence)
        ? row.evidence.filter((entry): entry is string => typeof entry === 'string')
        : [],
    },
  ];
}
